import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Gestion de produits";
const heroImage = "https://net-basis.net/Bilder/ict-product-management-market-adaptation-modular-development.webp";
const heroAlt = "Des mains présentent un cube modulaire à blocs rouges pour la gestion de produits TIC.";
const heroClaimLine1 = "Parfaitement adapté !";
const heroClaimLine2 = "Positionné en toute sécurité !";
const widget2 = "Votre partenaire expérimenté pour une gestion de produits adaptée au marché et une adaptation locale des produits dans la région DACH. Nous aidons les fabricants étrangers à aligner précisément leurs solutions technologiques sur les exigences du marché germanophone.";
const benefitsTitle = "Comment vous profitez de notre expertise en __gestion de produits__ :";
const p1 = "L'introduction et la gestion de produits sur de nouveaux marchés confrontent les entreprises à des défis complexes. Des exigences réglementaires distinctes, des situations concurrentielles locales et des besoins clients divergents requièrent une compréhension approfondie des marchés cibles. Sans une expertise locale solide et une compétence éprouvée en gestion de produits sur place, les entreprises risquent des erreurs d'investissement, des opportunités de marché manquées et une utilisation inefficace des ressources.";
const p2Text = "NetBasis International intervient donc précisément ici et met un savoir-faire complet à la disposition de ses fabricants partenaires : Nous prenons en charge l'intégralité de la gestion de produits dans la région DACH – de l'analyse de marché à l'adaptation des produits, jusqu'au suivi continu et à l'optimisation de votre portefeuille de produits.";
const p2Parts = p2Text.split(COMPANY_NAME);
const p3 = "Depuis Düsseldorf, nous veillons à ce que vos produits soient parfaitement adaptés aux exigences locales. Nous coordonnons l'ensemble des processus relatifs aux produits, surveillons le respect des réglementations légales et assurons une communication fluide entre votre maison mère et les acteurs de la région commerciale. Vous êtes ainsi positionné de manière compétitive dès le premier jour.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Gestion de produits professionnelle sans création de structures propres sur place",
  "Adaptation des produits au marché selon les exigences et normes locales",
  "Observation continue du marché et analyse de la concurrence",
  "Gestion efficace du cycle de vie de l'ensemble de votre portefeuille de produits",
  "Optimisation des chaînes d'approvisionnement et des processus de vente",
  "Garantie de la conformité réglementaire dans la région DACH",
  "Soulagement de votre maison mère des tâches opérationnelles de gestion de produits",
  "Accès aux réseaux locaux, aux prestataires de services et aux canaux de distribution",
  "Réactivité rapide face aux évolutions du marché et aux retours clients",
  "Solution évolutive – s'adaptant de manière flexible à vos objectifs de croissance",
];

export default function GestionDeProduitsFrPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Gestion de Produits | NetBasis International"
        description="Adaptation ciblée et positionnement de vos produits pour les exigences spécifiques des marchés cibles DACH."
        ogUrl="https://net-basis.net/fr/services/developpement-commercial/gestion-de-produits"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-center overflow-hidden pointer-events-none pt-12 sm:pt-16 md:pt-24 lg:pt-20">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-center px-4 leading-[1.15] md:leading-[1.5] pm-hero-text-slide"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  <span className="block sm:inline">{heroClaimLine1}</span>
                  <span className="hidden sm:inline"> </span>
                  <span className="block sm:inline">{heroClaimLine2}</span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="text-foreground-950 font-semibold text-sm md:text-base mb-4 leading-relaxed">
              {widget2}
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {(() => {
                  const parts = p1.split(COMPANY_NAME);
                  return parts.map((part, i) => (
                    <span key={i}>
                      {part}
                      {i < parts.length - 1 && (
                        <strong className="text-primary-600">{COMPANY_NAME}</strong>
                      )}
                    </span>
                  ));
                })()}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{COMPANY_NAME}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>{p3}</p>
            </div>

            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}